import React from 'react';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import ResponsiveDialogue from '../../ResponsiveDialogue/ResponsiveDialogue';
import RoundedButton from '../../RoundedButton/RoundedButton';
import
{
	KEEP_ALIVE_MODAL_BODY,
	KEEP_ALIVE_MODAL_LEAVE,
	KEEP_ALIVE_MODAL_STAY,
	KEEP_ALIVE_MODAL_TITLE,
} from '../../../constants/messages';

interface KeepAliveModalProps
{
	open: boolean;
	remainingMs: number;
	totalMs: number;
	onStay: () => void;
	onLeave: () => void;
}

/** Formats a duration in milliseconds as `M:SS`. */
const formatRemaining = (ms: number): string =>
{
	const safe = Math.max(0, ms);
	const minutes = Math.floor(safe / 60000);
	const seconds = Math.floor(safe / 1000) % 60;
	return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const KeepAliveModal: React.FC<KeepAliveModalProps> = ({
	open,
	remainingMs,
	totalMs,
	onStay,
	onLeave,
}) =>
{
	const progressValue = totalMs > 0
		? Math.max(0, Math.min(100, (remainingMs / totalMs) * 100))
		: 0;

	// Explicit user choice required — backdrop click is suppressed inside
	// ResponsiveDialogue, and ESC is disabled below. onClose is a no-op so that
	// no accidental dismissal path exists.
	const handleClose = () => { /* explicit user choice required */ };

	return (
		<ResponsiveDialogue
			data-testid="keep-alive-modal"
			open={open}
			onClose={handleClose}
			title={KEEP_ALIVE_MODAL_TITLE}
			showCloseButton={false}
			disableEscapeKeyDown
		>
			<Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
				<Box sx={{ position: 'relative', display: 'inline-flex' }}>
					<CircularProgress
						variant="determinate"
						value={progressValue}
						size={120}
						thickness={4}
						data-testid="keep-alive-modal-progress"
					/>
					<Box
						sx={{
							top: 0,
							left: 0,
							bottom: 0,
							right: 0,
							position: 'absolute',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'center',
						}}
					>
						<Typography
							variant="h5"
							component="div"
							data-testid="keep-alive-modal-countdown"
						>
							{formatRemaining(remainingMs)}
						</Typography>
					</Box>
				</Box>
			</Box>
			<Box sx={{ px: 2, pb: 1 }}>
				<Typography variant="body1" align="center">
					{KEEP_ALIVE_MODAL_BODY}
				</Typography>
			</Box>
			<Divider sx={{ my: 2 }} />
			<Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
				<Box sx={{ pr: 2 }}>
					<RoundedButton
						variantStyle="light"
						variant="outlined"
						data-testid="keep-alive-modal-leave-button"
						onClick={onLeave}
					>
						{KEEP_ALIVE_MODAL_LEAVE}
					</RoundedButton>
				</Box>
				<Box>
					<RoundedButton
						variantStyle="dark"
						variant="contained"
						data-testid="keep-alive-modal-stay-button"
						onClick={onStay}
					>
						{KEEP_ALIVE_MODAL_STAY}
					</RoundedButton>
				</Box>
			</Box>
		</ResponsiveDialogue>
	);
};

export default KeepAliveModal;
